/**
 * Filterable tutorial title directory used by the tutorial blog template.
 * The element is intentionally self-contained so it reconnects automatically
 * when blog-tags.js replaces #Blog-Content after an AJAX tag change.
 */
if (!customElements.get("blog-directory")) {
  customElements.define(
    "blog-directory",
    class BlogDirectory extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.input = this.querySelector("[data-tutorial-directory-input]");
        this.clearButton = this.querySelector(
          "[data-tutorial-directory-clear]",
        );
        this.count = this.querySelector("[data-tutorial-directory-count]");
        this.empty = this.querySelector("[data-tutorial-directory-empty]");
        this.details = this.querySelector(".tutorial-directory__details");
        this.groups = Array.from(
          this.querySelectorAll("[data-tutorial-directory-group]"),
        );
        this.items = Array.from(
          this.querySelectorAll("[data-tutorial-directory-item]"),
        );
        this.total = this.items.length;

        if (!this.input) return;
        this.initialized = true;

        this.mobileQuery = window.matchMedia("(max-width: 989px)");
        this.syncDisclosure(this.mobileQuery);
        if (this.mobileQuery.addEventListener) {
          this.mobileQuery.addEventListener("change", (event) =>
            this.syncDisclosure(event),
          );
        } else {
          this.mobileQuery.addListener((event) => this.syncDisclosure(event));
        }

        if (!this.items.length) {
          this.input.disabled = true;
          if (this.empty) {
            this.empty.textContent =
              "No directory items have been added yet.";
            this.empty.classList.remove("hidden");
          }
          return;
        }

        this.items.forEach((item) => {
          const title =
            item.dataset.title ||
            item.querySelector("a")?.textContent ||
            item.textContent ||
            "";
          item._normalizedTitle = this.normalize(title);
        });

        this.groups.forEach((group) => {
          group._directoryItems = Array.from(
            group.querySelectorAll("[data-tutorial-directory-item]"),
          );
        });

        this.input.addEventListener("input", () => this.filter());
        this.input.addEventListener("search", () => this.filter());
        this.input.addEventListener("keydown", (event) => {
          if (event.key === "Escape" && this.input.value) {
            event.preventDefault();
            this.clear();
          }
        });

        if (this.clearButton) {
          this.clearButton.addEventListener("click", () => this.clear());
        }
      }

      normalize(value) {
        return String(value)
          .normalize("NFKD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLocaleLowerCase()
          .trim();
      }

      syncDisclosure(viewport) {
        if (!this.details) return;

        const isMobile = viewport.matches;
        if (isMobile && this.lastViewportWasMobile !== true) {
          this.details.open = false;
        } else if (!isMobile) {
          this.details.open = true;
        }
        this.lastViewportWasMobile = isMobile;
      }

      filter() {
        const term = this.normalize(this.input.value);
        const tokens = term.split(/\s+/).filter(Boolean);
        let visible = 0;

        this.items.forEach((item) => {
          const matches = tokens.every((token) =>
            item._normalizedTitle.includes(token),
          );
          item.hidden = !matches;
          if (matches) visible += 1;
        });

        this.groups.forEach((group) => {
          const groupVisible = group._directoryItems.filter(
            (item) => !item.hidden,
          ).length;
          const groupCount = group.querySelector(
            "[data-tutorial-directory-group-count]",
          );

          if (groupCount) {
            groupCount.textContent = String(
              tokens.length
                ? groupVisible
                : groupCount.dataset.total || group._directoryItems.length,
            );
          }

          if (tokens.length) {
            if (group._openBeforeSearch === undefined) {
              group._openBeforeSearch = group.open;
            }
            group.hidden = groupVisible === 0;
            group.open = groupVisible > 0;
          } else {
            group.hidden = false;
            if (typeof group._openBeforeSearch === "boolean") {
              group.open = group._openBeforeSearch;
              group._openBeforeSearch = undefined;
            }
          }
        });

        if (this.count) this.count.textContent = String(visible);
        if (this.empty) this.empty.classList.toggle("hidden", visible !== 0);
        if (this.clearButton) {
          this.clearButton.classList.toggle("hidden", term.length === 0);
        }
      }

      clear() {
        this.input.value = "";
        this.filter();
        this.input.focus();
      }
    },
  );
}
